/**
 * The one place that talks to the API.
 *
 * The token lives in `sessionStorage`, not in a cookie and not in `localStorage`: it is the
 * analyst's own credential, it should not survive closing the browser, and putting it in a cookie
 * would invite CSRF on an API that deliberately does not use one.
 */

import type {
  ActivityOut,
  AssessmentOut,
  CaseOut,
  DecisionOut,
  DocumentOut,
  EnrichmentFinding,
  EvidenceDetail,
  Metrics,
  Calibration,
  CalibrationFinding,
  CasePage,
  NoteOut,
  PolicyImpact,
  PolicyOut,
  PolicyPayload,
  ProfileOut,
  ReportOut,
  InlineReport,
  RiskScoreOut,
  RunDiff,
  RunOut,
  UserOut,
  VerificationOut,
} from "./types";

const BASE = "/api/v1";
const TOKEN_KEY = "bancor.token";
const SIGNED_OUT_KEY = "bancor.signedout";

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly raw: string,
  ) {
    super(message);
  }
}

export function getToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null; // private mode, storage disabled: the app asks to sign in again
  }
}

export function setToken(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(TOKEN_KEY, token);
    else sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    /* nothing to do: the session simply will not persist across reloads */
  }
}

/**
 * Whether this tab signed out on purpose.
 *
 * "Cerrar sesión" has to mean the sign-in screen, and in development it did not: the dev proxy
 * signs every request, so the app asked who it was, got an answer, and walked straight back in.
 * Recording the intent — rather than relying on the next request failing — makes the button mean
 * the same thing wherever it runs.
 */
export function wasSignedOut(): boolean {
  try {
    return sessionStorage.getItem(SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function setSignedOut(value: boolean): void {
  try {
    if (value) sessionStorage.setItem(SIGNED_OUT_KEY, "1");
    else sessionStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    /* the app still shows the sign-in screen for as long as this tab lives */
  }
}

/** What to tell the analyst, from what the API said. See `friendlyError` for the wording. */
const MESSAGES: [RegExp, string][] = [
  [
    /invalid token|missing bearer|401/i,
    "Tu sesión no tiene acceso. Volvé a ingresar tu clave o pedí a soporte que revise tu usuario.",
  ],
  [
    /failed to fetch|networkerror|load failed/i,
    "No se puede conectar con el servicio de análisis. Reintentá en unos segundos; si sigue igual, avisá a soporte.",
  ],
  [
    /403|forbidden/i,
    "No tenés permiso para esta sección. La política de crédito la edita un referente o un administrador.",
  ],
  [/not found|404/i, "No se encontró lo que buscabas. Puede haber sido eliminado."],
  [
    /unsupported|mime/i,
    "Ese tipo de archivo no se puede procesar. Se aceptan PDF, Word, Excel e imágenes.",
  ],
  [
    /500|internal server error/i,
    "El servicio tuvo un error inesperado. Avisá a soporte indicando la hora.",
  ],
];

export const GENERIC_ERROR = "No se pudo completar la operación.";

export function friendlyError(error: unknown): {
  message: string;
  detail: string;
} {
  const raw = error instanceof Error ? error.message : String(error);
  for (const [pattern, message] of MESSAGES) {
    if (pattern.test(raw)) return { message, detail: raw };
  }
  return { message: GENERIC_ERROR, detail: raw };
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = getToken();
  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      ...init,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError("Failed to fetch", 0, String(e));
  }

  if (!response.ok) {
    const raw = await response.text();
    if (response.status === 401) expired();
    throw new ApiError(detailOf(raw) || `HTTP ${response.status}`, response.status, raw);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

const RETURN_TO = "bancor.returnto";

/**
 * La sesión venció en medio de algo.
 *
 * Lo que estaba escrito ya está a salvo en su borrador; lo que falta es no dejar al analista mirando
 * un error de acceso en la pantalla donde estaba. Se recuerda dónde estaba, se limpia el token, y la
 * aplicación muestra el ingreso — y al volver aterriza en el mismo legajo, no en el inicio.
 */
function expired(): void {
  if (!getToken() && !wasSignedOut()) return; // nunca estuvo adentro: no hay sesión que haya vencido
  try {
    sessionStorage.setItem(RETURN_TO, window.location.pathname + window.location.search);
  } catch {
    /* sin almacenamiento se vuelve al inicio, que es aceptable */
  }
  setToken(null);
  window.dispatchEvent(new CustomEvent("bancor:session-expired"));
}

export function takeReturnTo(): string | null {
  try {
    const path = sessionStorage.getItem(RETURN_TO);
    sessionStorage.removeItem(RETURN_TO);
    return path;
  } catch {
    return null;
  }
}

function detailOf(body: string): string {
  try {
    const parsed = JSON.parse(body);
    const detail = parsed?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length) {
      const first = detail[0];
      const field = (first.loc ?? []).slice(1).join(".");
      return `${field}: ${first.msg ?? "dato inválido"}`;
    }
  } catch {
    /* not JSON */
  }
  return body.slice(0, 200);
}

async function blob(path: string): Promise<Blob> {
  return (await download(path)).blob;
}

/** Los filtros de la cola. Viajan al servidor porque la cartera no entra en el navegador. */
export interface CaseFilters {
  archived?: boolean;
  q?: string;
  view?: string | null;
  grade?: string | null;
  assignedTo?: string | null;
  decided?: boolean;
}

export function caseParams(options: CaseFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (options.archived) params.set("archived", "true");
  if (options.q?.trim()) params.set("q", options.q.trim());
  if (options.view) params.set("view", options.view);
  if (options.grade) params.set("grade", options.grade);
  if (options.assignedTo) params.set("assigned_to", options.assignedTo);
  if (options.decided !== undefined) params.set("decided", String(options.decided));
  return params;
}

export interface DownloadedFile {
  blob: Blob;
  /** The name the API says the file should be saved as, when it says so. */
  filename: string | null;
}

/** A file plus the name the server gave it, so the naming convention lives in one place. */
async function download(path: string): Promise<DownloadedFile> {
  const token = getToken();
  const response = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!response.ok) throw new ApiError(`HTTP ${response.status}`, response.status, "");
  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get("content-disposition")),
  };
}

/** The filename out of a Content-Disposition header, preferring the RFC 5987 form when present. */
export function filenameFrom(header: string | null): string | null {
  if (!header) return null;
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded?.[1]) {
    try {
      return decodeURIComponent(encoded[1].trim().replace(/^"|"$/g, "")) || null;
    } catch {
      /* a malformed header is not worth failing a download over */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain?.[1]?.trim() || null;
}

export const api = {
  // --- who is signed in ---
  login: (email: string, password: string) =>
    request<{ token: string; user: UserOut }>("/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<void>("/auth/logout", { method: "POST" }),
  whoami: () => request<UserOut>("/auth/me"),
  listUsers: () => request<UserOut[]>("/auth/users"),

  // --- the queue ---
  metrics: () => request<Metrics>("/metrics"),
  /**
   * Una página de la cartera. El servidor filtra y pagina: pedir todo para dibujar cincuenta filas
   * es lo que hacía la versión anterior, y es lo que deja de funcionar con una cartera real.
   */
  listCases: (options: CaseFilters & { page?: number; size?: number } = {}) => {
    const params = caseParams(options);
    if (options.page) params.set("page", String(options.page));
    if (options.size) params.set("size", String(options.size));
    const query = params.toString();
    return request<CasePage>(`/cases${query ? `?${query}` : ""}`);
  },
  // La exportación toma los mismos filtros que la lista a propósito: la planilla que baja el
  // analista tiene que ser lo que está mirando, no la página que quedó en pantalla.
  exportCases: (options: CaseFilters = {}) => {
    const query = caseParams(options).toString();
    return download(`/cases/export.csv${query ? `?${query}` : ""}`);
  },
  bulk: (caseIds: string[], action: "assign" | "archive" | "restore", userId?: string | null) =>
    request<{ changed: number }>("/cases/bulk", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        case_ids: caseIds,
        action,
        user_id: userId ?? null,
      }),
    }),
  getCase: (id: string) => request<CaseOut>(`/cases/${id}`),
  createCase: (cuit: string, legal_name: string, notes: string | null) =>
    request<CaseOut>("/cases", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ cuit, legal_name, notes }),
    }),
  uploadDocuments: (caseId: string, files: File[], docType: string | null) => {
    const form = new FormData();
    files.forEach((f) => form.append("files", f, f.name));
    const query = docType ? `?doc_type=${encodeURIComponent(docType)}` : "";
    return request<DocumentOut[]>(`/cases/${caseId}/documents${query}`, {
      method: "POST",
      body: form,
    });
  },
  updateCase: (caseId: string, body: { legal_name?: string; cuit?: string; notes?: string }) =>
    request<CaseOut>(`/cases/${caseId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  archiveCase: (caseId: string) => request<void>(`/cases/${caseId}/archive`, { method: "POST" }),
  restoreCase: (caseId: string) => request<void>(`/cases/${caseId}/restore`, { method: "POST" }),
  assignCase: (caseId: string, userId: string | null) =>
    request<void>(`/cases/${caseId}/assign`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId }),
    }),
  removeDocument: (documentId: string) =>
    request<void>(`/documents/${documentId}`, { method: "DELETE" }),

  // --- what the analyst decides and records ---
  getDecision: (caseId: string) => request<DecisionOut | null>(`/cases/${caseId}/decision`),
  recordDecision: (
    caseId: string,
    body: { outcome: string; rationale: string; conditions: string[] },
  ) =>
    request<DecisionOut>(`/cases/${caseId}/decision`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  listNotes: (caseId: string) => request<NoteOut[]>(`/cases/${caseId}/notes`),
  addNote: (caseId: string, body: string) =>
    request<NoteOut>(`/cases/${caseId}/notes`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    }),
  listVerifications: (caseId: string) =>
    request<VerificationOut[]>(`/cases/${caseId}/verifications`),
  verifyFigure: (
    caseId: string,
    body: {
      field_name: string;
      period: string | null;
      agrees: boolean;
      comment?: string | null;
    },
  ) =>
    request<VerificationOut>(`/cases/${caseId}/verifications`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  unverifyFigure: (caseId: string, fieldName: string, period: string | null) => {
    const query = new URLSearchParams({ field_name: fieldName });
    if (period) query.set("period", period);
    return request<void>(`/cases/${caseId}/verifications?${query}`, {
      method: "DELETE",
    });
  },
  listActivity: (caseId: string) => request<ActivityOut[]>(`/cases/${caseId}/activity`),

  startRun: (caseId: string, fromStep: string | null) =>
    request<{ id: string }>(`/cases/${caseId}/runs`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ from_step: fromStep }),
    }),
  getRun: (runId: string) => request<RunOut>(`/runs/${runId}`),
  getProfile: (caseId: string) => request<ProfileOut>(`/cases/${caseId}/profile`),
  // `runId` matters: without it the API returns the findings of every run the legajo ever had,
  // and a legajo that was retried shows the same BCRA answer two or three times.
  getEnrichment: (caseId: string, runId?: string | null) =>
    request<EnrichmentFinding[]>(
      `/cases/${caseId}/enrichment${runId ? `?run_id=${encodeURIComponent(runId)}` : ""}`,
    ),
  getScore: (caseId: string) => request<RiskScoreOut>(`/cases/${caseId}/score`),
  // Sin parámetros compara las dos últimas corridas, que es lo que se quiere mirar el 99% de las
  // veces: qué se movió cuando el legajo se volvió a analizar.
  getDiff: (caseId: string) => request<RunDiff>(`/cases/${caseId}/diff`),
  getAssessment: (caseId: string) => request<AssessmentOut>(`/cases/${caseId}/assessment`),
  listReports: (caseId: string) => request<ReportOut[]>(`/cases/${caseId}/reports`),
  // El informe para leer en su lugar: el mismo HTML que se descarga, con las citas navegables.
  inlineReport: (caseId: string) => request<InlineReport>(`/cases/${caseId}/report/inline`),
  getEvidence: (id: string) => request<EvidenceDetail>(`/evidence/${id}`),
  sendFeedback: (reportId: string, rating: "up" | "down", comment: string | null) =>
    request<unknown>(`/reports/${reportId}/feedback`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rating, comment }),
    }),
  downloadReport: (reportId: string) => download(`/reports/${reportId}/download`),
  // --- la política de crédito ---
  policy: () => request<PolicyOut>("/policy"),
  policies: () => request<PolicyOut[]>("/policies"),
  calibration: () => request<Calibration>("/policy/calibration"),
  proposal: (indicatorKey: string, band: string) =>
    request<{ payload: PolicyPayload; based_on: CalibrationFinding }>(
      "/policy/calibration/proposal",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ indicator_key: indicatorKey, band }),
      },
    ),
  previewPolicy: (payload: PolicyPayload) =>
    request<PolicyImpact>("/policies/preview", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payload, reason: "" }),
    }),
  publishPolicy: (payload: PolicyPayload, reason: string) =>
    request<PolicyOut>("/policies", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payload, reason, activate: true }),
    }),
  activatePolicy: (version: string) =>
    request<PolicyOut>(`/policies/${version}/activate`, { method: "POST" }),

  documentUrl: (documentId: string) => `${BASE}/documents/${documentId}/file`,
  documentBlob: (documentId: string) => blob(`/documents/${documentId}/file`),
};
